import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import { api } from "@/lib/api";
import useCaptcha from "@/hooks/useCaptcha";

const INITIAL = {
  full_name: "",
  role_or_title: "",
  email: "",
  phone_number: "",
  organization_name: "",
  event_name: "",
  event_date: "",
  event_location: "",
  audience_size: "",
  audience_description: "",
  engagement_type: "",
  fee_range: "",
  travel_covered: "",
  notes: "",
  website: "",
};

const REQUIRED = ["full_name", "email", "organization_name", "event_name"];

function Tag({ kind = "required" }) {
  return (
    <span className={`wt-label-tag ${kind}`}>{kind === "required" ? "Required" : "Optional"}</span>
  );
}

export default function BookSpeakingPage() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const { captcha, reload } = useCaptcha();

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) {
      setErrors((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    const next = {};
    REQUIRED.forEach((k) => {
      if (!form[k] || !form[k].toString().trim()) next[k] = "This field is required.";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Please enter a valid email address.";
    if (!captchaAnswer || isNaN(Number(captchaAnswer))) next.captcha = "Please solve the math question.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/speaking-inquiries", {
        ...form,
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: Number(captchaAnswer),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(", ")
            : "Something went wrong. Please try again.",
      );
      setCaptchaAnswer("");
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="book-speaking-page">
        <PageHeader
          eyebrow="Speaking request received"
          title="Thank you for the invitation."
          lead="Wanakee reviews each speaking request personally and will follow up with availability and next steps."
          testId="speaking-confirmation-header"
        />
        <section className="pb-16">
          <div className="wt-container">
            <article
              data-testid="speaking-confirmation"
              className="wt-card p-6 sm:p-8 max-w-2xl"
              style={{ borderLeft: "3px solid var(--wt-primary)" }}
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={22} className="mt-0.5" style={{ color: "var(--wt-primary)" }} />
                <div>
                  <p className="wt-eyebrow">Confirmation</p>
                  <p className="mt-2 wt-body">
                    Your speaking request has been received. Wanakee will review the details and
                    follow up with availability and next steps.
                  </p>
                </div>
              </div>
              <div className="mt-6 wt-hairline" />
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to="/" className="wt-btn-outline">Back to home</Link>
                <Link to="/services" className="wt-btn-primary">Explore Services</Link>
              </div>
            </article>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="book-speaking-page">
      <PageHeader
        eyebrow="Book Me to Speak"
        title="Invite Wanakee to your room."
        lead="Keynotes, workshops, panels, retreats, and youth experiences. Share the event details and Wanakee will follow up directly."
        testId="book-speaking-header"
      />
      <section className="pb-16">
        <div className="wt-container">
          <form data-testid="speaking-form" onSubmit={onSubmit} noValidate className="max-w-3xl space-y-7">
            <div className="hidden" aria-hidden="true">
              <input value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" />
            </div>

            <fieldset className="space-y-5">
              <legend className="wt-eyebrow">About you</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="full_name"><span>Full name</span><Tag /></label>
                  <input id="full_name" data-testid="speaking-input-full_name" className="wt-input" value={form.full_name} onChange={set("full_name")} aria-invalid={!!errors.full_name} />
                  {errors.full_name && <p className="text-xs" style={{ color: "#872c1f" }}>{errors.full_name}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="email"><span>Email address</span><Tag /></label>
                  <input id="email" type="email" data-testid="speaking-input-email" className="wt-input" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} />
                  {errors.email && <p className="text-xs" style={{ color: "#872c1f" }}>{errors.email}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="role_or_title"><span>Role or title</span><Tag kind="optional" /></label>
                  <input id="role_or_title" data-testid="speaking-input-role" className="wt-input" value={form.role_or_title} onChange={set("role_or_title")} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="phone_number"><span>Phone number</span><Tag kind="optional" /></label>
                  <input id="phone_number" data-testid="speaking-input-phone" className="wt-input" value={form.phone_number} onChange={set("phone_number")} />
                </div>
              </div>
            </fieldset>

            <div className="wt-hairline" />

            <fieldset className="space-y-5">
              <legend className="wt-eyebrow">About the event</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="organization_name"><span>Organization</span><Tag /></label>
                  <input id="organization_name" data-testid="speaking-input-organization" className="wt-input" value={form.organization_name} onChange={set("organization_name")} aria-invalid={!!errors.organization_name} />
                  {errors.organization_name && <p className="text-xs" style={{ color: "#872c1f" }}>{errors.organization_name}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="event_name"><span>Event name</span><Tag /></label>
                  <input id="event_name" data-testid="speaking-input-event" className="wt-input" value={form.event_name} onChange={set("event_name")} aria-invalid={!!errors.event_name} />
                  {errors.event_name && <p className="text-xs" style={{ color: "#872c1f" }}>{errors.event_name}</p>}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="event_date"><span>Event date</span><Tag kind="optional" /></label>
                  <input id="event_date" type="text" placeholder="e.g. April 14, 2026" data-testid="speaking-input-date" className="wt-input" value={form.event_date} onChange={set("event_date")} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="event_location"><span>Event location</span><Tag kind="optional" /></label>
                  <input id="event_location" data-testid="speaking-input-location" className="wt-input" value={form.event_location} onChange={set("event_location")} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="audience_size"><span>Audience size</span><Tag kind="optional" /></label>
                  <input id="audience_size" data-testid="speaking-input-audience-size" className="wt-input" value={form.audience_size} onChange={set("audience_size")} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="engagement_type"><span>Paid or pro bono</span><Tag kind="optional" /></label>
                  <select id="engagement_type" data-testid="speaking-select-engagement" className="wt-input" value={form.engagement_type} onChange={set("engagement_type")}>
                    <option value="">Select</option>
                    <option value="Paid">Paid</option>
                    <option value="Honorarium">Honorarium</option>
                    <option value="Pro bono">Pro bono</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="fee_range"><span>Fee or honorarium range</span><Tag kind="optional" /></label>
                  <input id="fee_range" data-testid="speaking-input-fee" className="wt-input" value={form.fee_range} onChange={set("fee_range")} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="wt-label" htmlFor="travel_covered"><span>Is travel covered</span><Tag kind="optional" /></label>
                  <select id="travel_covered" data-testid="speaking-select-travel" className="wt-input" value={form.travel_covered} onChange={set("travel_covered")}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Not sure">Not sure</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="wt-label" htmlFor="audience_description"><span>Tell us about the audience</span><Tag kind="optional" /></label>
                  <textarea id="audience_description" rows={3} data-testid="speaking-textarea-audience" className="wt-input" value={form.audience_description} onChange={set("audience_description")} />
                </div>
                <div className="flex flex-col gap-2 sm:col-span-2">
                  <label className="wt-label" htmlFor="notes"><span>Anything else Wanakee should know</span><Tag kind="optional" /></label>
                  <textarea id="notes" rows={4} data-testid="speaking-textarea-notes" className="wt-input" value={form.notes} onChange={set("notes")} />
                </div>
              </div>
            </fieldset>

            <div className="wt-hairline" />

            <fieldset className="space-y-3">
              <legend className="wt-eyebrow">Quick check</legend>
              <div className="flex flex-col gap-2 max-w-xs">
                <label className="wt-label" htmlFor="captcha"><span>{`What is ${captcha.a} + ${captcha.b}?`}</span><Tag /></label>
                <input id="captcha" type="number" inputMode="numeric" data-testid="speaking-captcha" className="wt-input" value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} aria-invalid={!!errors.captcha} />
                {errors.captcha && <p className="text-xs" style={{ color: "#872c1f" }}>{errors.captcha}</p>}
              </div>
            </fieldset>

            <div className="pt-3 flex items-center gap-3">
              <button type="submit" disabled={submitting} data-testid="speaking-submit" className="wt-btn-primary h-12 px-7 text-base">
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Submitting…" : "Submit Speaking Request"}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
